import { Theme, ThemeOptions } from '@mui/material';
import MuiButton, {
    buttonClasses,
    ButtonProps as MuiButtonProps
} from '@mui/material/Button';
import MuiButtonBase from '@mui/material/ButtonBase';
import * as React from 'react';

/* eslint-disable no-unused-vars */
declare module '@mui/material/Button' {
    interface ButtonClasses {
        sizeSmaller: string;
    }

    interface ButtonPropsSizeOverrides {
        smaller: true;
    }
}
/* eslint-enable no-unused-vars */

import { Box } from '../box';
import { useMergeRefs, useOverflow } from '../hooks';
import { Skeleton } from '../skeleton';
import { styled } from '../styled';
import { Tooltip } from '../tooltip';
import { firstLetterUppercase } from '../typography';
import { BaseProps, enumType, makeEnum, withVersion } from '../utils';
export { MuiButtonBase as ButtonBase };

export type ButtonProps = MuiButtonProps &
    BaseProps & {
        skeleton?: boolean;
        target?: '_blank';
        loading?: boolean;
    };

const variants = [
    'contained',
    'outlined',
    'text'
] satisfies ButtonProps['variant'][];

const sizes = [
    'smaller',
    'small',
    'medium',
    'large'
] satisfies ButtonProps['size'][];

export const ButtonVariant = makeEnum(...variants);
export type ButtonVariant = enumType<typeof ButtonVariant>;

export const ButtonSize = makeEnum(...sizes);
export type ButtonSize = enumType<typeof ButtonSize>;

const StyledMuiButton = styled(MuiButton)<{
    $skeleton?: boolean;
    $childrenIsString?: boolean;
}>((props) => {
    const overflowProps = {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        display: 'inline-block'
    } satisfies React.CSSProperties;

    return {
        textTransform: 'none',
        pointerEvents: props.$skeleton ? 'none' : undefined,
        minWidth: 0,
        ...(props.$childrenIsString ? { whiteSpace: 'nowrap' } : {}),
        ...(!props.startIcon && !props.endIcon && props.$childrenIsString
            ? overflowProps
            : {}),
        [`.${buttonClasses.loadingIndicator}`]: {
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
        },
        [`.${buttonClasses.startIcon} [data-role="icon"], .${buttonClasses.endIcon} [data-role="icon"]`]:
            {
                fill: 'currentColor',
                color: 'inherit'
            },
        ...firstLetterUppercase
    };
});

const ButtonImpl = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ skeleton, ...props }, ref) => {
        const innerRef = useMergeRefs(ref);
        const { textOverflow } = useOverflow(innerRef);

        return (
            <Tooltip title={props.children} bypass={!textOverflow}>
                <StyledMuiButton
                    ref={innerRef}
                    data-role="button"
                    disableElevation
                    $childrenIsString={typeof props.children === 'string'}
                    $skeleton={skeleton}
                    {...props}
                >
                    {props.children}
                    {skeleton && (
                        <Box
                            component="span"
                            bgcolor="background.paper"
                            position="absolute"
                            width="100%"
                            height="100%"
                            left={0}
                            top={0}
                        >
                            <Skeleton width="100%" height="100%" />
                        </Box>
                    )}
                </StyledMuiButton>
            </Tooltip>
        );
    }
);

export const Button = withVersion(ButtonImpl, '1.3.0');

export * from './icon-button';

export const buttonStyle = {
    MuiButton: {
        styleOverrides: {
            sizeSmaller: ({ theme }: { theme: Theme }) => ({
                fontSize: theme.palette.components?.button?.small?.fontSize,
                fontWeight:
                    theme.palette?.components?.button?.small?.fontWeight,
                padding: '0 4px',
                [`.${buttonClasses.endIcon}`]: { marginLeft: 4 }
            }),
            sizeSmall: ({ theme }: { theme: Theme }) => ({
                fontSize: theme.palette.components?.button?.small?.fontSize,
                fontWeight: theme.palette.components?.button?.small?.fontWeight
            }),
            sizeMedium: ({ theme }: { theme: Theme }) => ({
                fontSize: theme.palette.components?.button?.medium?.fontSize,
                fontWeight: theme.palette.components?.button?.medium?.fontWeight
            }),
            sizeLarge: ({ theme }: { theme: Theme }) => ({
                fontSize: theme.palette.components?.button?.large?.fontSize,
                fontWeight: theme.palette.components?.button?.large?.fontWeight
            })
        }
    }
} satisfies ThemeOptions['components'];
